import {ReactElement, useEffect, useState, useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectUserType, setPreviousUserType} from 'app/store/users/userSlice';
import {useMsal} from '@azure/msal-react';
import {setLoading, setInactiveLogout} from 'app/store/auth/authSlice';
import {Backdrop, Button, Box, IconButton, Snackbar, SnackbarCloseReason, Typography, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const InactivityLogoutTimer = () => {
  const theme = useTheme();
  const userType = useSelector(selectUserType);
  const {instance} = useMsal();
  const [content, setContent] = useState<ReactElement | undefined>(undefined);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [showLogoutMessage, setShowLogoutMessage] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onLogout = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setInactiveLogout());
    dispatch(setPreviousUserType(userType));
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    await instance.logoutRedirect({
      account: account ?? undefined,
      logoutHint: account?.username,
      postLogoutRedirectUri: window.location.origin + '/logout',
    });

    return Promise.resolve();
  }, [dispatch, instance, userType]);

  const checkForInactivity = useCallback(() => {
    const expireTimeString = localStorage.getItem('expireTime');
    const expireTime = expireTimeString ? parseInt(expireTimeString) : 0;

    if (expireTime - Date.now() <= 60000 && expireTime - Date.now() >= 50000) {
      setShowWarning(true);
    }

    if (expireTime < Date.now()) {
      setShowWarning(false);
      localStorage.setItem('inactivityLogout', 'true');
      onLogout();
    }
  }, [setShowWarning, onLogout]);

  const updateExpiryTime = () => {
    if (localStorage.getItem('inactivityLogout') === 'true') return;
    const expireTime = Date.now() + 15 * 60 * 1000; // 15 minutes in miliseconds
    localStorage.setItem('expireTime', expireTime.toString());
  };

  const handleSnackbarClose = useCallback(
    (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      event?.stopPropagation();
      if (reason === 'clickaway') {
        return;
      }
      setShowWarning(false);
      setShowLogoutMessage(false);
      localStorage.setItem('inactivityLogout', 'false');
    },
    [setShowWarning, setShowLogoutMessage],
  );

  const LogoutMessage = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '20px',
          width: '470px',
          height: '202px',
          background: theme.palette.grey[200],
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
          <Typography variant='h1' sx={{fontSize: '24px', width: '314px'}}>
            You have been logged out due to inactivity.
          </Typography>
          <IconButton
            aria-label='close'
            color='inherit'
            sx={{
              height: '40px',
              width: '40px',
              marginTop: '-20px',
              marginRight: '-20px',
              '&:hover': {backgroundColor: 'white'},
            }}
            onClick={handleSnackbarClose}
          >
            <CloseIcon
              sx={{
                width: '14px',
                height: '14px',
              }}
            />
          </IconButton>
        </Box>
        <Button
          variant='contained'
          color='primary'
          size='small'
          sx={{
            textTransform: 'none',
            width: '170px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '700',
            alignSelf: 'flex-end',
          }}
          onClick={handleSnackbarClose}
        >
          Log back in
        </Button>
      </Box>
    ),
    [handleSnackbarClose, theme],
  );

  const WarningMessage = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '20px',
          width: '470px',
          height: '202px',
          background: theme.palette.grey[200],
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
          <Typography variant='h1' sx={{fontSize: '24px', width: '314px'}}>
            You will be logged out due to inactivity in 1 minute.
          </Typography>
          <IconButton
            aria-label='close'
            color='inherit'
            sx={{
              height: '40px',
              width: '40px',
              marginTop: '-20px',
              marginRight: '-20px',
              '&:hover': {backgroundColor: 'white'},
            }}
            onClick={handleSnackbarClose}
          >
            <CloseIcon
              sx={{
                width: '14px',
                height: '14px',
              }}
            />
          </IconButton>
        </Box>
        <Button
          variant='contained'
          color='primary'
          size='small'
          sx={{
            textTransform: 'none',
            width: '170px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '700',
            alignSelf: 'flex-end',
          }}
          onClick={handleSnackbarClose}
        >
          Keep me logged in
        </Button>
      </Box>
    ),
    [handleSnackbarClose, theme],
  );

  useEffect(() => {
    if (userType) {
      updateExpiryTime();
      localStorage.setItem('inactivityLogout', 'false');
      const interval = setInterval(() => {
        checkForInactivity();
      }, 5000); // Check for inactivity every 5 seconds

      return () => clearInterval(interval);
    } else if (localStorage.getItem('inactivityLogout') === 'true') {
      setShowLogoutMessage(true);
      setShowWarning(false);
    }
  }, [userType, checkForInactivity]);

  useEffect(() => {
    const events = ['click', 'keypress', 'scroll', 'mousemove'];

    const update = () => {
      updateExpiryTime();
    };

    events.forEach((event) => window.addEventListener(event, update));

    return () => {
      events.forEach((event) => window.removeEventListener(event, update));
    };
  }, []);

  useEffect(() => {
    if (showWarning) {
      setContent(WarningMessage);
    } else if (showLogoutMessage) {
      setContent(LogoutMessage);
    } else {
      setContent(undefined);
    }
  }, [LogoutMessage, WarningMessage, showWarning, showLogoutMessage, setContent]);

  return (
    <Backdrop open={showWarning || showLogoutMessage} sx={(theme) => ({zIndex: theme.zIndex.drawer + 1})}>
      <Snackbar
        sx={{height: '100%'}}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        onClose={handleSnackbarClose}
        open={showWarning || showLogoutMessage}
        autoHideDuration={showLogoutMessage ? null : 60000}
      >
        {content}
      </Snackbar>
    </Backdrop>
  );
};

export default InactivityLogoutTimer;
